<template>
    <div class="concept-input pt-xs pb-md px-md" :class="{ closed: isCollapsed }">
        <p v-if="props.type === 'concept'" class="color-white text-center mb-xs mt-xs">
            Search based on key technology area or conceptual idea
        </p>
        <form
            id="context-target-add-concept"
            class="display-flex flex-column"
            @submit.prevent="submitConcept()"
        >
            <div
                v-if="props.altLayout"
                class="top display-flex justify-start align-items-center cursor-pointer"
                @click="toggleCollapsedInput()"
            >
                <font-awesome-icon
                    class="text-stone-400 pr-xs mt-n1 icon"
                    icon="fas fa-angle-down"
                />
                <p class="font-bold text-primary mb-xxs w-100 text-left">Custom Concept</p>
            </div>
            <div class="collapsed-content mt-xxs">
                <p class="text-left text-xs text-stone-800 mb-xs">
                    {{ props.instructionText }}
                </p>
                <AonTextarea
                    class="w-100 concept-textarea pa-0"
                    v-model="conceptForm.concept"
                    :error="v$.concept.$error"
                    :error-options="{
                        errorMsg: v$.concept.$errors[0] ? v$.concept.$errors[0].$message : null,
                    }"
                    focus
                />
                <AonButton
                    :class="[
                        { 'align-self-start mt-xs': props.altLayout },
                        { 'align-self-center mt-md': !props.altLayout },
                    ]"
                    :label="props.type === 'concept' ? 'Generate Results' : 'Add Concept Element'"
                    is-form-submit
                    width="100%"
                    :loading="conceptLoading || altLayoutAddLoading"
                />
            </div>
        </form>
    </div>
</template>

<script setup>
import { inject, ref, reactive, onBeforeUnmount, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useArenaStore } from '@/stores'

import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'
import { requestConceptAlignment } from '@/api/productAlignment.js'
import { typedPlaceholder } from '@/lib/helpers'

import AonTextarea from '@moatmetrics/armory/src/components/base/forms/AonTextarea'

const eventBus = inject('eventBus')
const router = useRouter()
const arenaStore = useArenaStore()

const emit = defineEmits(['add-node-to-arena'])
const props = defineProps({
    type: {
        type: String,
        default: 'concept',
    },
    altLayout: {
        type: Boolean,
        default: false,
    },
    instructionText: {
        type: String,
        default: 'Create a custom concept and add it to the Arena.',
    },
    maxLength: {
        type: Number,
        default: 200,
    },
    ignoreHeightConstraint: {
        type: Boolean,
        default: false,
    },
})

const placeholder = ref(null)
const conceptLoading = ref(false)
const altLayoutAddLoading = ref(false)
const userForcedCollapse = ref(false)

const conceptForm = reactive({
    concept: '',
})
const rules = {
    concept: { required, maxLength: maxLength(props.maxLength) },
}
const v$ = useVuelidate(rules, conceptForm)

onMounted(() => {
    placeholder.value?.kill()
    initAnimatedPlaceholder()
})

onBeforeUnmount(() => {
    placeholder.value.kill()
})

const isCollapsed = computed(() => {
    if (props.ignoreHeightConstraint) {
        return false
    }

    if (!arenaStore.forceConceptInputClose) {
        return userForcedCollapse.value
    }

    if (arenaStore.forceConceptInputClose && userForcedCollapse.value) {
        return false
    }

    if (arenaStore.forceConceptInputClose && !userForcedCollapse.value) {
        return true
    }
})

const submitConcept = async (source = null) => {
    const validInputs = await v$.value.$validate()

    if (!validInputs) {
        return
    }
    altLayoutAddLoading.value = true

    if (props.type === 'arena') {
        emit('add-node-to-arena', { data: conceptForm.concept, source: source })
        altLayoutAddLoading.value = false
        return
    }

    conceptLoading.value = true
    let response
    try {
        response = await requestConceptAlignment(conceptForm.concept)
    } catch (err) {
        logger.error(err)

        eventBus.emit('snacktime', {
            type: 'error',
            message: err,
        })
    } finally {
        placeholder.value.kill()
        router.push({
            name: 'Arena Output',
            params: { jobPk: response.data.productAlignmentId },
        })
        conceptLoading.value = false
        altLayoutAddLoading.value = false
    }
}

const initAnimatedPlaceholder = () => {
    placeholder.value = new typedPlaceholder({
        placeholders: [
            '"Rocket powered backpack in personal aviation"',
            '"Waterjets with Advanced Hydrodynamics in the context of Marine Propulsion Systems"',
            '"Time travel"',
        ],
        preText: 'Ex: ',
        stay: 1000,
        speed: 30,
        element: '.concept-textarea',
        static: arenaStore.selectedNode ? true : false,
    })
    placeholder.value.init()
    // "focus" param not working
    let focusEl = document.querySelector('.concept-textarea')
    focusEl
        ? focusEl.querySelector('.input-wrapper').getElementsByTagName('textarea')[0].focus()
        : ''
}

const toggleCollapsedInput = () => {
    userForcedCollapse.value = !userForcedCollapse.value
}
</script>

<style lang="scss" scoped>
@use '@moatmetrics/armory/src/sass/responsive.scss';

.concept-input {
    width: 100%;
    text-align: center;
    height: 230px;
    background: var(--color-moat-100);

    transition: all 0.3s ease-in-out;
    transition-delay: 0.1s;

    &.closed {
        height: 46px;
        transition-delay: 0.3s;
        background: white;

        .top {
            .icon {
                transform: rotate(-90deg);
            }
        }

        .collapsed-content {
            pointer-events: none;
            opacity: 0;
            transform: translateY(-30px);

            transition-delay: 0.1s;
        }
    }

    .concept-textarea {
        padding: 10px;

        &::placeholder {
            color: var(--color-stone-200);
        }
    }

    .top {
        cursor: pointer;

        .icon {
            transform: rotate(0);
            transform-origin: 10px;

            transition: all 0.3s ease-in-out;
        }
    }

    .collapsed-content {
        transition: all 0.3s ease-in-out;
        transition-delay: 0.3s;
    }

    :deep(.aon-textarea) {
        textarea {
            min-height: 80px;
        }
    }
}
</style>
